import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { action } from '@storybook/addon-actions';
import type { Meta, StoryFn } from '@storybook/react';

import { Banner, createStringMatcher, type BannerHandleValue } from '@pega/cosmos-react-core';
import { Stakeholders } from '@pega/cosmos-react-work';
import type { StakeholdersProps, Stakeholder } from '@pega/cosmos-react-work';

import {
  AddAndEditForm,
  mockNetworkReq,
  mockRoles,
  useMockStakeholders
} from './Stakeholders.mocks';

export default {
  title: 'Work/Stakeholders',
  component: Stakeholders
} as Meta;

interface StakeHoldersStoryProps {
  hideAddNew?: boolean;
  formErrorBanner?: boolean;
}

export const StakeHoldersDemo: StoryFn<StakeHoldersStoryProps> = (args: StakeHoldersStoryProps) => {
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<undefined | 'add' | 'viewAll'>();
  const [editing, setEditing] = useState<Stakeholder['id'] | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [viewAllLoading, setViewAllLoading] = useState(false);
  const [viewAllSearchValue, setViewAllSearchValue] = useState('');
  const [banners, setBanners] = useState<StakeholdersProps['form']['banners']>(null);
  const bannerHandleRef = useRef<BannerHandleValue>(null);
  const [hasServerError, setHasServerError] = useState(false);

  // Grab some mock data...
  const [mockStakeholders, setMockStakeholders] = useMockStakeholders(() => {
    setLoading(false);
  });

  const [editData, setEditData] = useState<
    StakeholdersProps['form']['rendererProps'] | undefined
  >();

  // Merges built-in available actions with stakeholder objects.
  const addActions = useCallback(
    (items: Stakeholder[]) => {
      return items.map(({ role, data, ...stakeholder }) => {
        return {
          ...stakeholder,
          onEdit: () => {
            setFormLoading(true);
            setEditing(stakeholder.id);
            setEditData(mockStakeholders.find(ms => ms.id === stakeholder.id)?.data);
            mockNetworkReq().then(() => setFormLoading(false));
          },
          onRemove: () => {
            setMockStakeholders(cur => cur.filter(({ id }) => id !== stakeholder.id));
          }
        };
      });
    },
    [mockStakeholders]
  );

  const form: StakeholdersProps['form'] = useMemo(() => {
    return {
      // Set this to true when fetching view metadata for the form or during submit validation.
      loading: formLoading,
      // Represents "available" roles for a stakeholder. Set to undefined when choosing not to present them as options.
      roles: mode === 'add' ? mockRoles : undefined,
      // Represents the current role of a stakeholder if they have one. When undefined the first role in the roles array with be selected in the UI.
      currentRole: editing ? mockStakeholders.find(({ id }) => id === editing)?.role : undefined,
      /**
       * A function that will be invoked by React's createElement. If only a simple function is needed then an inline arrow is ok.
       * If local state(hooks) is desired best to define a stable component definition outside of the parent component's function body.
       */
      renderer: AddAndEditForm,
      rendererProps: editData,
      banners,
      onSubmit: ({ selectedRoleName, closeForm }) => {
        setFormLoading(true);
        action(`The selected role is: ${selectedRoleName}`);
        // Create or update the stakeholder resource.
        const req = mockNetworkReq(args.formErrorBanner);
        req
          .then(() => {
            closeForm();
          })
          .catch(() => {
            setBanners(
              <Banner
                variant='urgent'
                messages={['500: Unexpected error']}
                handle={bannerHandleRef}
              />
            );
            setHasServerError(true);
          })
          .finally(() => {
            setFormLoading(false);
          });
      },
      onDismiss: ({ closeForm }) => {
        closeForm();
      },
      onAfterClose: () => {
        // Clean up loading state when the form is closed.
        setFormLoading(false);
        setEditing(null);
        setEditData(undefined);
        setMode(undefined);
        setBanners(null);
        setHasServerError(true);
      }
    };
  }, [mode, formLoading, mockRoles, editing, editData, banners]);

  const viewAll: StakeholdersProps['viewAll'] = useMemo(() => {
    let viewAllItems = mockStakeholders;

    // When the user has entered a search value we can filter the array or api request.
    if (viewAllSearchValue) {
      // For client side searches cosmos offers utils such as createStringMatcher for creating RegExp with params.
      const filterRegex = createStringMatcher(viewAllSearchValue, 'boundary');
      // Asserting as string since, in this demo, primary is a string. But type is a non nullable ReactNode.
      viewAllItems = mockStakeholders.filter(({ primary }) => filterRegex.test(primary as string));
    }

    return {
      // Set this to true when fetching data for the view all modal.
      loading: viewAllLoading,
      // Even when loading is true this must be an array, empty or not.
      items: addActions(viewAllItems),
      // The search string will be handed back as the arg, pass a state setter and filter off of that.
      onSearch: setViewAllSearchValue,
      onAfterClose: () => {
        // Clean up loading state when the modal is closed.
        if (viewAllLoading) setViewAllLoading(false);
        setViewAllSearchValue('');
      }
    };
  }, [viewAllLoading, mockStakeholders, viewAllSearchValue, addActions]);

  useEffect(() => {
    // Orchestrate based on mode state if needed.
  }, [mode]);

  useEffect(() => {
    // Orchestrate based on editing state if needed.
  }, [editing]);

  useEffect(() => {
    if (hasServerError) {
      // Wait for banner to be rendered before calling focus.
      bannerHandleRef.current?.focus();
    }
  }, [hasServerError]);

  return (
    <Stakeholders
      loading={loading}
      items={useMemo(
        () => addActions(mockStakeholders.slice(0, 3)),
        [addActions, mockStakeholders]
      )}
      count={loading ? undefined : mockStakeholders.length}
      onAddNew={
        args.hideAddNew
          ? undefined
          : () => {
              setMode('add');
              setFormLoading(true);
              mockNetworkReq().then(() => setFormLoading(false));
            }
      }
      form={form}
      onViewAll={() => {
        setMode('viewAll');
        setViewAllLoading(true);
        mockNetworkReq().then(() => setViewAllLoading(false));
      }}
      viewAll={viewAll}
    />
  );
};

StakeHoldersDemo.args = {
  hideAddNew: false,
  formErrorBanner: false
};

StakeHoldersDemo.argTypes = {
  hideAddNew: { control: { type: 'boolean' } },
  formErrorBanner: { control: { type: 'boolean' } }
};
